import React, { useState, useEffect, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, Input } from '@chapche/ui';
import { LOCALE_EN, LOCALE_RU } from '../../../constant';
import LocaleSwitcher from '../../../shared/LocaleSwitcher';
import { v4 as uuid } from 'uuid';
import { propertiesService } from '../../../services/propertiesService';
import { propertiesStorage } from '../../../services/propertiesStorage';
import { MinusIcon } from '../../../appearence/icons/minus_icon';

type LocaleFieldMap = {
    [LOCALE_RU]: string;
    [LOCALE_EN]: string;
};

type PropertyValueView = {
    id?: string;
    selectValues?: string[];
    value?: string;
    valueNames?: unknown[];
};

export type PropertyItemValueSaveService = {
    getProperty: (propertyId?: string) => PropertyValueView | undefined;
    registerItemUpdateObserver: (observer: () => void) => void;
    unRegisterItemUpdateObserver: (observer: () => void) => void;
    updateValueLocalization: (
        locale: string,
        value: string,
        code: string | undefined,
        propertyId?: string,
    ) => void;
    updatePropertyValueField: (value: string, propertyId?: string) => void;
    deleteSelectValue: (value: string) => void;
};

type PropertyItemValueProps = {
    itemSaveService: PropertyItemValueSaveService;
    propertyId?: string;
    index: number;
};

const createValueCode = (): string => uuid().slice(0, 4) + uuid().slice(0, 4);

const PropertyItemValue = ({
    itemSaveService,
    propertyId,
    index,
}: PropertyItemValueProps) => {
    const { i18n } = useTranslation();
    const [fieldValue, setFieldValue] = useState<LocaleFieldMap>({
        [LOCALE_RU]: '',
        [LOCALE_EN]: '',
    });
    const [selectedLocale, setSelectedLocale] = useState(LOCALE_RU);

    const resolveDefinitionPropertyId = (): string | undefined => {
        if (propertyId) {
            return propertyId;
        }
        return itemSaveService.getProperty?.()?.id;
    };

    const resolvePropertyForLocalization = (): unknown => {
        // Property edit page: prefer in-memory detail (GET /property/:id) over list storage.
        if (!propertyId) {
            const current = itemSaveService.getProperty?.();
            if (current?.id) {
                return current;
            }
        }
        const definitionId = resolveDefinitionPropertyId();
        if (definitionId) {
            return propertiesStorage.getById(definitionId) || definitionId;
        }
        return itemSaveService.getProperty?.();
    };

    const resolveValueCode = (): string | undefined => {
        const current = itemSaveService.getProperty?.();
        if (current?.selectValues) {
            return current.selectValues[index];
        }
        return itemSaveService.getProperty?.(propertyId)?.value;
    };

    const localizedOrCode = (valueCode: string | undefined, locale: string): string => {
        const propertyOrId = resolvePropertyForLocalization();
        return propertiesService.getPropertyValueLocalization(
            valueCode,
            locale,
            propertyOrId,
        );
    };

    const changeSelectValues = (e: ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ ...fieldValue, [selectedLocale]: e.target.value });
        itemSaveService.updateValueLocalization(
            selectedLocale,
            e.target.value,
            resolveValueCode(),
            resolveDefinitionPropertyId(),
        );
    };

    const changePropertyData = () => {
        const propertyData = itemSaveService.getProperty?.(propertyId);
        if (propertyData?.selectValues || propertyData?.value) {
            const valueKeyItem = propertyData.selectValues
                ? itemSaveService.getProperty()?.selectValues?.[index]
                : propertyData.value;
            setFieldValue({
                [LOCALE_EN]: localizedOrCode(valueKeyItem, LOCALE_EN),
                [LOCALE_RU]: localizedOrCode(valueKeyItem, LOCALE_RU),
            });
        } else if (!propertyId) {
            // New select property: seed first value code once selectValues is still empty.
            itemSaveService.updatePropertyValueField(createValueCode());
        }
    };

    useEffect(() => {
        itemSaveService.registerItemUpdateObserver(changePropertyData);
        changePropertyData();
        return () => itemSaveService.unRegisterItemUpdateObserver(changePropertyData);
        // Mount-only: observers + initial hydrate (same as previous JSX).
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
    }, []);

    const createNewValue = () => {
        const property = itemSaveService.getProperty();
        if (
            property
            && property.selectValues
            && property.selectValues.length - 1 === index
            && !propertyId
        ) {
            itemSaveService.updatePropertyValueField(createValueCode());
        }
    };

    const selectValues = itemSaveService.getProperty()?.selectValues;
    const canDelete = index !== 0 && Boolean(selectValues?.[index]);

    return (
        <label
            className={cn(
                'block__text product__form-label product__form-value',
                !propertyId && 'product__form-value_property',
            )}
        >
            {propertyId ? null : (
                <span>{i18n.t('product.scheme.property.code.valueOfData')}</span>
            )}
            <Input
                fullWidth
                className={cn(
                    'product__form-input mt-0',
                    fieldValue[LOCALE_EN] && fieldValue[LOCALE_RU] && 'product__form-input_full-filled',
                )}
                placeholder={i18n.t('app.page.product.propertyItem.selectValues')}
                value={fieldValue[selectedLocale] || ''}
                onBlur={createNewValue}
                onChange={changeSelectValues}
            />
            <LocaleSwitcher localeCode={selectedLocale} notifyListener={setSelectedLocale} />
            {canDelete ? (
                <span
                    onClick={() => {
                        const code = itemSaveService.getProperty()?.selectValues?.[index];
                        if (code) {
                            itemSaveService.deleteSelectValue(code);
                        }
                    }}
                >
                    <MinusIcon nameOfClass="product__value-icon" />
                </span>
            ) : null}
        </label>
    );
};

export default PropertyItemValue;
